import { Component, ElementRef, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { format, subYears } from 'date-fns';
import { Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { GenderEnum } from '~/auth/enum/gender.enum';
import { User } from '~/auth/model/user';
import { UserService } from '~/auth/services/user/user.service';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { CtaWidthEnum } from '~/component/call-to-action/enums/width.enum';
import { ActivityService } from '~/core/services/activity/activity.service';
import { AgeRequirementService } from '~/core/services/activity/age-requirement.service';
import { FormControlService } from '~/core/services/form/form-control.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { TitleService } from '~/core/services/title/title.service';
import { MajorityValidator } from '~/core/validators/majority.validator';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
})
export class ProfileComponent implements OnInit {
  public readonly ctaType = CtaButtonTypeEnum.Submit;
  public readonly ctaWidth = CtaWidthEnum.Responsive;
  public readonly femaleValue = GenderEnum.FEMALE;
  public readonly maleValue = GenderEnum.MALE;
  public readonly undefinedValue = GenderEnum.NOT_KNOWN;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;

  public consent = false;
  public profileForm: FormGroup;
  public user: User;

  constructor(
    private readonly userService: UserService,
    private readonly formBuilder: FormBuilder,
    private readonly redirectionService: RedirectionService,
    public appProfileElementRef: ElementRef,
    private readonly majorityValidator: MajorityValidator,
    private readonly formControlService: FormControlService,
    private readonly ageRequirement: AgeRequirementService,
    private readonly localizeRouter: LocalizeRouterService,
    private readonly titleService: TitleService,
    private readonly activityService: ActivityService,
    private readonly activatedRoute: ActivatedRoute
  ) {}

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.checkout.profile.title');

    this.user = this.userService.getUser();

    const birthdate = this.user.getBirthdate()
      ? format(this.user.getBirthdate(), 'yyyy-MM-dd')
      : null;

    this.profileForm = this.formBuilder.group({
      givenName: [
        this.user.givenName,
        [
          Validators.required,
          noHtmlTagValidator(),
          Validators.maxLength(40),
          // exclude special characters
          Validators.pattern(/^[^`~!@#$%\\^&*()_+={}|[\]:;<>?,./]*$/),
        ],
      ],
      familyName: [
        this.user.familyName,
        [
          Validators.required,
          noHtmlTagValidator(),
          Validators.maxLength(40),
          // exclude special characters
          Validators.pattern(/^[^`~!@#$%\\^&*()_+={}|[\]:;<>?,./]*$/),
        ],
      ],
      gender: [this.user.gender, Validators.required],
      birthdate: [
        birthdate,
        [Validators.required, this.majorityValidator.validateISODate()],
      ],
      consent: [this.consent],
    });

    this.activityService.getConsents().subscribe((consents) => {
      this.consent = consents.purposes.every(
        (purpose) => purpose.enabled === true
      );

      this.profileForm.setValue({
        ...this.profileForm.value,
        consent: this.consent,
      });
    });
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouter.translateRoute('/checkout.summary').toString(),
    };
  }

  public get gender(): string {
    return this.profileForm.controls.gender.value;
  }

  public get majorityBirthDate(): string {
    const date = new Date();
    const majority = +this.ageRequirement.tenantMajority;
    const majorityDate = subYears(date, majority);
    return format(majorityDate, 'yyyy-MM-dd');
  }

  public errors(formControlName: string): ValidationErrors | null {
    const control = this.profileForm.get(formControlName);
    return control?.touched ? control?.errors : null;
  }

  public onSubmit(): Subscription {
    if (this.profileForm.invalid) {
      return;
    }

    return this.userService
      .updateUser({ ...this.user, ...this.profileForm.value })
      .pipe(
        mergeMap(() => this.activityService.getConsents()),
        map((consents) => {
          consents.purposes.forEach(
            (purpose) => (purpose.enabled = this.consent)
          );
          return consents;
        }),
        mergeMap((updatedConsents) =>
          this.activityService.putConsents(updatedConsents)
        ),
        mergeMap(() => this.activatedRoute.queryParams)
      )
      .subscribe(({ to = 'billing-address' }) => {
        this.redirectionService.redirect(`/checkout.${to}`);
      });
  }
}
